/* eslint-disable no-mixed-operators */
import * as React from "react";
import { IPersonaProps } from "@fluentui/react/lib/Persona";
import {
  IBasePicker,
  NormalPeoplePicker,
  ValidationState,
} from "@fluentui/react/lib/Pickers";
import _, { filter, isArray } from "lodash";
import { IPeoplePickerState, IPeoplePickerProps, PrincipalType, suggestionProps } from "./types";
import { queryPeopleAndGroups, searchGroups, searchPeople } from "../../../lib/graph";
import { connect } from "react-redux";
import { RootState } from "../../../store/store";
import { IGroup, IUser } from "@microsoft/mgt-react";
import { BaseComponent, Spinner, SpinnerSize } from "@fluentui/react";


class PeoplePicker extends BaseComponent<
  IPeoplePickerProps,
  IPeoplePickerState
> {
  // All pickers extend from BasePicker specifying the item type.
  private _picker = React.createRef<IBasePicker<IPersonaProps>>();
  private _event = "none";
  private _clipboardData = "";

  constructor(props: any) {
    super(props);

    this.state = {
      currentPicker: 1,
      delayResults: false,
      peopleList: [],
      currentSelectedItems: [],
      isValid: null,
      isLoading: false,
    };
  }

  static defaultProps = {
    defaultValue: [],
    principalType: PrincipalType.People,
    itemLimit: undefined,
    isRequired: false,
    errorMessage: "",
    disabled: false,
    readonly: false
  }

  async componentDidMount() {
    this.setState({ isLoading: true });
    await this.loadPersona();
    this.setState({ isLoading: false });
  }

  async componentDidUpdate(prevProps: IPeoplePickerProps) {
    // Check if objectIds or defaultValue props have changed
    if (!_.isEqual(this.props.objectIds, prevProps.objectIds) || 
        !_.isEqual(this.props.defaultValue, prevProps.defaultValue)) {
      this.setState({ isLoading: true });
      await this.loadPersona();
      this.setState({ isLoading: false });
    }
  }

  loadPersona = async () => {
    const { objectIds, defaultValue } = this.props
    let persona: IPersonaProps[] = !!defaultValue && [...defaultValue] || []
    if (!!objectIds && objectIds.length > 0) {
      if (objectIds.length > 0) {
        const personaList: IPersonaProps[] = await queryPeopleAndGroups(objectIds, this.props.principalType)
        persona.push(...personaList)
      }
    }
    // Remove duplicates based on tertiaryText (id)
    const distinctPersona = persona.filter((item, index, self) => 
      index === self.findIndex(p => p.tertiaryText === item.tertiaryText)
    );
    this.setState({ currentSelectedItems: distinctPersona });
  }

  public render() {
    let currentPicker: JSX.Element;

    currentPicker = this._renderNormalPicker();

    return <div>{currentPicker}</div>;
  }

  private _getTextFromItem(persona: IPersonaProps): string {
    return persona.text as string;
  }

  private _renderNormalPicker() {
    let errorMessageStyle: React.CSSProperties = {
      fontFamily:
        '"BoschFont","Segoe UI", "Segoe UI Web (West European)", "Segoe UI", -apple-system, BlinkMacSystemFont, Roboto, "Helvetica Neue", sans-serif',
      fontSize: "12px",
      fontWeight: 400,
      color: "#a80000",
      margin: "0px",
      paddingTop: "5px",
      display: "flex",
      alignItems: "center",
    };
    return (
      <div className={this.state.isValid === false ? "invalid" : ""}>
        {!!this.state.isLoading && (
          <Spinner size={SpinnerSize.small} label="Searching users..." />
        )}
        <NormalPeoplePicker
          styles={{
            input: {
              display: this.props.readonly && 'none' || 'inline-block',
            },
            text: {
              '::after': {
                background: this.props.readonly && 'none' || 'rgba(218, 218, 218, 0.29)'
              }
            }
          }}
          itemLimit={this.props.itemLimit}
          selectedItems={this.state.currentSelectedItems}
          onResolveSuggestions={this._onFilterChanged}
          getTextFromItem={this._getTextFromItem}
          pickerSuggestionsProps={suggestionProps}
          className={"ms-PeoplePicker"}
          key={"normal"}
          onRemoveSuggestion={this._onRemoveSuggestion}
          onValidateInput={this._validateInput}
          removeButtonAriaLabel={"Remove"}
          inputProps={{
            onPaste: (ev) => this._onPasteValue(ev.clipboardData),
            "aria-label": "People Picker",
            placeholder:
              this.props.readonly ? "" : this.props.principalType === PrincipalType.Any ? "Start typing a name..." : this.props.principalType === PrincipalType.People ? "Enter names or email addresses..." : "Enter department names..."
          }}
          removeButtonIconProps={{ iconName: this.props.readonly && 'none' || 'Cancel' }}
          componentRef={this._picker}
          onInputChange={this._onInputChange}
          resolveDelay={300}
          onChange={(items: IPersonaProps[] | undefined) => {
            this.setState({ currentSelectedItems: items }, () => {
              if (this.props.onChange) {
                this.props.onChange(items);
              }
            })
          }}
          disabled={this.props.disabled || this.props.readonly}
        />
        {!!this.props.errorMessage && <p className="ms-TextField-errorMessage" style={errorMessageStyle}>
          {this.props.errorMessage}
        </p>}
      </div>
    );
  }

  public validate() {
    let result = true;
    if (this.props.isRequired === true) {
      if (
        !_.isUndefined(this.state.currentSelectedItems)
          ? this.state.currentSelectedItems.length === 0
          : true
      ) {
        result = false;
        this.setState({
          isValid: false,
        });
      }
    }
    return result;
  }

  public _onPasteValue(clipboardData: DataTransfer) {
    this._event = "paste";
    this._clipboardData = clipboardData
      .getData("Text")
      .replace(/(?:\r\n|\r|\n)/g, "");
  }

  private _filterPeople = async (filterText: string): Promise<IPersonaProps[]> => {
    let users: IUser[] = await searchPeople(filterText);
    if (!!users && users.length > 0) {
      let peoplePickerItems: IPersonaProps[] = []
      users.forEach(user => {
        peoplePickerItems.push({
          text: user.displayName!,
          showSecondaryText: true,
          secondaryText: user.userPrincipalName!,
          tertiaryText: user.id,
        })
      })
      return peoplePickerItems
    }
    return []
  }

  private _filterGroups = async (filterText: string): Promise<IPersonaProps[]> => {
    let groups: IGroup[] = await searchGroups(filterText)
    if (!!groups && groups.length > 0) {
      let peoplePickerItems: IPersonaProps[] = []
      groups.forEach(group => {
        peoplePickerItems.push({
          text: group.displayName!,
          showSecondaryText: true,
          secondaryText: group.mail!,
          tertiaryText: group.id,
        })
      })
      return peoplePickerItems
    }
    return []
  }

  private listContainsPersona(persona: IPersonaProps, personas: IPersonaProps[]) {
    if (!personas || !personas.length || personas.length === 0) {
      return false;
    }
    return personas.filter(item => item.text === persona.text).length > 0;
  }

  private removeDuplicates(personas: IPersonaProps[], possibleDupes: IPersonaProps[] | undefined) {
    return !!possibleDupes ? personas.filter(persona => !this.listContainsPersona(persona, possibleDupes)) : personas;
  }


  private _onFilterChanged = async (
    filterText: string,
    currentPersonas: IPersonaProps[] | undefined,
    limitResults?: number
  ): Promise<IPersonaProps[]> => {
    let filteredPersonas: IPersonaProps[] = [];
    if (filterText) {
      switch (this.props.principalType) {
        case PrincipalType.People:
          filteredPersonas = await this._filterPeople(filterText);
          break;
        case PrincipalType.Group:
          filteredPersonas = await this._filterGroups(filterText);
          break;
        case PrincipalType.Any:
          let foundPeople = await this._filterPeople(filterText);
          let foundGroups = await this._filterGroups(filterText);
          filteredPersonas = [...foundPeople, ...foundGroups]
          break;
        default:
          filteredPersonas = []
          break;
      }

    } else {
      filteredPersonas = [];
    }
    filteredPersonas = this.removeDuplicates(filteredPersonas, currentPersonas)
    return filteredPersonas;
  }

  private _onRemoveSuggestion = (item: IPersonaProps): void => {
    const { currentSelectedItems } = this.state;
    if (!currentSelectedItems) {
      return;
    }
    const indexPeopleList: number = currentSelectedItems.indexOf(item);

    if (indexPeopleList >= 0) {
      const newPeople: IPersonaProps[] = currentSelectedItems
        .slice(0, indexPeopleList)
        .concat(currentSelectedItems.slice(indexPeopleList + 1));
      this.setState({ currentSelectedItems: newPeople });
    }
  };

  private _validateInput = (input: string): ValidationState => {
    if (input.indexOf("@") !== -1) {
      return ValidationState.valid;
    } else if (input.length > 1) {
      return ValidationState.warning;
    } else {
      return ValidationState.invalid;
    }
  };

  private _onInputChange(input: string): string {
    const outlookRegEx = /<.*>/g;
    const emailAddress = outlookRegEx.exec(input);

    if (emailAddress && emailAddress[0]) {
      return emailAddress[0].substring(1, emailAddress[0].length - 1);
    }

    return input;
  }

  // Get Selected Users/Departments
  public getSelectedItems() {
    const currentSelectedItems = this._picker.current?.items || undefined;
    let items: any[] = [];
    if (!_.isUndefined(currentSelectedItems)) {
      currentSelectedItems.forEach((item) => {
        items.push({
          displayName: item.text,
          id: item.tertiaryText,
          loginName: item.secondaryText,
        });
      });
    }
    return items;
  }

  // Set user/departments
  public setItems(items: any[]) {
    let _items: any[] = [];
    items.forEach((item: any) => {
      _items.push({
        text: item.displayName,
        showSecondaryText: true,
        secondaryText: item.loginName,
        tertiaryText: item.id,
      });
    });
    this.setState({ currentSelectedItems: _items });
  }

}
const mapStateToProps = (state: RootState) => ({
  userProfile: state.auth.userProfile
});
export default connect(mapStateToProps)(PeoplePicker);

